import { Component } from "react";
import { observer, Observer } from "mobx-react";
import { action, reaction, when } from "mobx";
import LayerStore from "@giscon/utils.layer-store";
import APIHelper from "@giscon/utils.api-helper-esri";
import { Typography } from "@mui/material";
import Legend from "./Legend";
import OpacitySlider from "./OpacitySlider";
import AccordionPanel from "components/AccordionPanel";
import mapServerExportMonitor from "utils/mapServerExportMonitor";
import { MAP_LAYER_TRANSITION_MESSAGE } from "utils/mapBusyIndicatorUtils";
import {
  autoSelectSingleChoiceForZeitabschnitt,
  findChoiceByValue,
  isSameValue,
  isSet,
  preserveZeitabschnittByLabel,
  CLEANUP_MAX_DELAY_MS,
  CLEANUP_MIN_DELAY_MS,
  CLEANUP_POLL_DELAY_MS,
  deactivateAllLayersExcept,
  DEFAULT_LAYER_OPACITY,
  dimAllLayersExcept,
  hasUnresolvedFilterValues as hasUnresolvedFilterValuesInSet,
  hasOtherManagedMapLayers,
  LAYERVIEW_SETTLE_FALLBACK_MS,
  layerMatchesConstraints,
  layerMatchesConstraintsExcept,
  NO_LAYER_CLEAR_DELAY_MS,
  normalizeLayerOpacity,
  OVERLAP_DIM_OPACITY,
  resolveLayerSource,
  resolveTargetLayerOpacity,
  shouldShowHiddenAttributeByStores,
  SYNC_DEBOUNCE_MS,
  TRANSITION_TIMEOUT_MS,
} from "utils/layerFilterUtils";

/**
 * LayerFilter manages raster-layer selection and visual transitions for climate data.
 *
 * Goal:
 * Keep one climate raster visible and switch to the next raster without blank flashes.
 *
 * What happens on each filter update:
 * 1) Resolve the target layer that matches the current filter values.
 * 2) Create or reuse the internal layer wrapper for that target layer.
 * 3) Resolve target opacity in this order:
 *    - operational opacity from store (if valid and > 0)
 *    - currently visible layer opacity (if > 0)
 *    - another managed layer currently on map (if > 0)
 *    - last remembered operational opacity (if > 0)
 *    - DEFAULT_LAYER_OPACITY as final fallback only
 * 4) Fast path (no switch currently running, target already visible):
 *    - keep target on map
 *    - apply resolved opacity
 *    - remove any leftover managed layers
 * 5) Switch path (target changed):
 *    - keep current image visible
 *    - add target layer hidden (opacity 0)
 *    - wait until target image is ready (layer view done + export traffic settled)
 *    - commit target opacity
 *    - keep old layers briefly at low opacity to avoid flicker
 *    - then remove old layers from the map
 * 6) Temporary no-match path:
 *    - if required primary selection is incomplete (Kategorie/Parameter/Zeitabschnitt),
 *      clear managed layers immediately (no fallback restore)
 *    - delay clearing briefly during filter settling
 *    - if values are still unresolved, keep/restore last stable layer
 *    - otherwise clear managed layers
 *
 * Important behavior guarantees:
 * - Old layers are not kept permanently. They are dimmed only for a short overlap,
 *   then removed.
 * - DEFAULT_LAYER_OPACITY is not always used. It is only the final fallback when
 *   no other valid opacity source is available.
 * - Only one switch is authoritative at a time, and stale async callbacks are ignored.
 */

class LayerFilter extends Component {
  constructor(props) {
    super(props);
    this.state = {
      legendPanelUserOverride: null,
    };

    this.layers = {}; //Speicherort für die layerwrapper(!)

    // Active-visibility state.
    this.currentVisibleLayerId = null;
    this._lastResolvedLayerId = null;
    this._lastShownLayerId = null;

    // Transition state (at most one in-flight layer swap at a time).
    this._transitionHandle = null; // ArcGIS layerView.watch handle
    this._transitionKeepId = null; // _internalID that should remain after swap
    this._transitionExportUnsubscribe = null;
    // Monotonic transition token used to reject stale async callbacks.
    this._activeTransitionSeq = 0;

    // Managed-layer bookkeeping.
    this._internalIDOffset = 1;
    this._configIdToInternalId = new Map();

    // Timers.
    this._syncDebounceTimer = null;
    this._noLayerClearTimer = null;
    this._postCommitCleanupTimer = null;

    // Opacity memory to prevent transient 0/default jumps during transitions.
    this._lastOperationalOpacity = DEFAULT_LAYER_OPACITY;

    // Cache legend payloads for logically equivalent slider selections
    // (same Kategorie/Parameter/mode on the same MapServer service).
    this._legendInfoCache = new Map();
    this._lastStableLegendInfo = null;
    this._lastStableLegendLayerUrl = null;
    this._lastStableLegendCacheKey = null;

    const communicationStore = this.props.CommunicationStore;

    // Ensure export interceptor is installed once before first transition.
    const installExportMonitor = mapServerExportMonitor.subscribe(() => {});
    if (typeof installExportMonitor === "function") {
      installExportMonitor();
    }

    communicationStore?.addMessage({
      to: "getLayerPossibilities",
      message: (excluded) => {
        return this.getLayerPossibilities(excluded);
      },
    });

    communicationStore?.addMessage({
      to: "filterAccess",
      message: (attribteId, filterChoices) => {
        return this.getFilterAccess(attribteId, filterChoices);
      },
    });
    communicationStore?.addMessage({
      to: "filterLevel",
      message: props.general.filterLevel,
    });
  }

  _clearStableLegend() {
    this._lastStableLegendInfo = null;
    this._lastStableLegendLayerUrl = null;
    this._lastStableLegendCacheKey = null;
  }

  _shouldKeepPreviousLegend(layerIdsLength) {
    if (!this._hasRequiredPrimarySelection()) {
      return false;
    }

    if (this._transitionKeepId !== null) {
      return true;
    }

    if (this._hasUnresolvedFilterValues()) {
      return true;
    }

    if (layerIdsLength === 0 && this._noLayerClearTimer !== null) {
      return true;
    }

    return false;
  }

  _handleLegendPanelExpandedChange = (expanded) => {
    this.setState((prevState) => {
      if (prevState.legendPanelUserOverride === expanded) {
        return null;
      }
      return { legendPanelUserOverride: expanded };
    });
  };

  _getLegendModeToken(attributeValues = {}) {
    const zeitraumStore = attributeValues["Zeitraum"];
    const selectedValue = zeitraumStore?.value;
    const choices = Array.isArray(zeitraumStore?.updateOptions?.choices)
      ? zeitraumStore.updateOptions.choices
      : [];

    const selectedChoice = choices.find((choice) => {
      if (!choice || selectedValue === undefined || selectedValue === null) {
        return false;
      }
      if (choice.value === selectedValue) {
        return true;
      }
      return String(choice.value) === String(selectedValue);
    });

    const selectedLabel = String(selectedChoice?.label || "");
    const selectedGroupId = String(selectedChoice?.GroupId || "").toLowerCase();
    const isChangeMode =
      selectedChoice?.excludeInSlider === true ||
      selectedGroupId === "aests" ||
      selectedLabel.includes("zu 1971-2000");

    return isChangeMode ? "m:change" : "m:base";
  }

  _getLegendCacheKey(layerConfig) {
    if (!layerConfig) {
      return null;
    }

    const attributeValues =
      this.props.CommunicationStore?.stores?.attributeEdit?.attributeValues ||
      {};

    const kategorieValue = attributeValues["Kategorie"]?.value;
    const parameterValue = attributeValues["Parameter"]?.value;

    if (
      kategorieValue === undefined ||
      kategorieValue === null ||
      kategorieValue === "" ||
      parameterValue === undefined ||
      parameterValue === null ||
      parameterValue === ""
    ) {
      return null;
    }

    const { layerBaseUrl } = resolveLayerSource({
      baseUrl: this.props.baseUrl,
      id: layerConfig.id,
      url: layerConfig.url,
    });
    const legendModeToken = this._getLegendModeToken(attributeValues);

    return [
      String(layerBaseUrl || "").toLowerCase(),
      `k:${String(kategorieValue)}`,
      `p:${String(parameterValue)}`,
      legendModeToken,
    ].join("|");
  }

  _storeLegendInCache(layerConfig, layerWrapper) {
    const legendCacheKey = this._getLegendCacheKey(layerConfig);
    if (!legendCacheKey) {
      return;
    }
    if (!layerWrapper?.legendInfo || layerWrapper?.legendState !== "loaded") {
      return;
    }

    this._legendInfoCache.set(legendCacheKey, layerWrapper.legendInfo);
  }

  _seedLegendFromCache(layerConfig, layerWrapper) {
    const legendCacheKey = this._getLegendCacheKey(layerConfig);
    if (!legendCacheKey || !layerWrapper) {
      return;
    }

    const cachedLegendInfo = this._legendInfoCache.get(legendCacheKey);
    if (!cachedLegendInfo) {
      return;
    }

    // Keep an already known legend visible while wrapper-level refresh runs.
    // Mutations must run inside a MobX action because legendInfo and legendState
    // are declared as observable in makeObservable (Layers.js).
    action(() => {
      if (!layerWrapper.legendInfo) {
        layerWrapper.legendInfo = cachedLegendInfo;
      }
      if (layerWrapper.legendState !== "loaded") {
        layerWrapper.legendState = "loaded";
      }
    })();
  }

  getFilterAccess = (attributeId, filterChoices = () => true) => {
    const { CommunicationStore, hideAsDropdown = [], general } = this.props;
    const { filterLevel = [] } = general;
    const attributeStore =
      CommunicationStore.stores?.attributeEdit?.attributeValues[attributeId];
    let index = undefined;
    let setIndex = () => {};
    let items = [];
    let visible = false;
    if (attributeStore) {
      const { choices = [] } = attributeStore.updateOptions || {};
      visible = attributeStore.visible;

      // Override visibility for hideAsDropdown attributes
      const overrideVisible = this.shouldShowHiddenAttribute(
        attributeId,
        choices,
        filterLevel,
        hideAsDropdown
      );
      if (overrideVisible !== null) {
        visible = overrideVisible;
      }

      items = choices.filter((c) => filterChoices(c));

      // Auto-select Zeitabschnitt when only one valid choice exists
      this.autoSelectSingleChoice(attributeId, items, attributeStore);

      index = choices
        .map(({ label, value }) => value)
        .indexOf(attributeStore.value); //der Umweg über indexof und dann value: die choices fangen oft nicht mit value 0 an!
      setIndex = (idx, pChoices) => {
        const choiceArray = Array.isArray(pChoices) ? pChoices : choices;
        if (!choiceArray || idx === undefined || idx === null) {
          return;
        }
        if (choiceArray[idx]) {
          attributeStore.setValue(choiceArray[idx].value);
        } else {
          console.warn(
            `LayerFilter.setIndex: requested index ${idx} out of bounds for attribute ${attributeId}`
          );
        }
      }; //(über Umwege) Setter im AttruteEdit Datastore
    }
    return {
      items: items,
      index: index,
      setIndex: setIndex,
      visible: visible,
    };
  };

  /** Auto-selects a single valid choice for 'Zeitabschnitt' when:
   * - attributeId is 'Zeitabschnitt'
   * - exactly one item remains after filtering
   * - attribute currently has no value
   * - the prerequisite 'Parameter' is already selected
   * - the sole item's label is not 'skip'
   */
  autoSelectSingleChoice(attributeId, items, attributeStore) {
    try {
      const communicationStore = this.props.CommunicationStore;
      const parameterStore =
        communicationStore?.stores?.attributeEdit?.attributeValues["Parameter"];
      return autoSelectSingleChoiceForZeitabschnitt({
        attributeId,
        items,
        attributeStore,
        parameterStore,
      });
    } catch (e) {
      console.warn("Auto-select Zeitabschnitt failed:", e);
    }
    return false;
  }

  /**
   * Determine visibility override for attributes configured as hideAsDropdown.
   *
   * This controls visibility for consumers of `getFilterAccess` (e.g., KlimaSliderWrapper,
   * PossibilityUpdater, statistic widgets), NOT the dropdown UI in CustomFilter.
   *
   * Attributes configured in hideAsDropdown will be visible only when all required
   * previous levels are considered satisfied.
   * A previous level is considered satisfied when any of the following is true:
   * - it has an explicit value
   * - all its available choices are labelled 'skip' (case-insensitive)
   * - it is itself configured as hideAsDropdown
   *
   * Example: if `Zeitraum` is in hideAsDropdown, the slider (KlimaSliderWrapper) will
   * only show when every earlier level in `filterLevel` (Kategorie, Parameter, Zeitabschnitt)
   * either has a value, is entirely composed of choices labelled "skip", or is also
   * configured as hideAsDropdown.
   *
   * @param {string} attributeId - attribute id being evaluated (e.g. 'Zeitraum', 'Szenario')
   * @param {Array} choices - current available choices for the attribute
   * @param {Array} filterLevel - ordered array of filter attribute ids (hierarchy)
   * @param {Array} hideAsDropdown - array of attribute ids configured as hideAsDropdown
   * @returns {boolean|null} true/false when visibility should be overridden, or null when no override applies
   */
  shouldShowHiddenAttribute(attributeId, choices, filterLevel, hideAsDropdown) {
    const communicationStore = this.props.CommunicationStore;
    return shouldShowHiddenAttributeByStores({
      attributeId,
      choices,
      filterLevel,
      hideAsDropdown,
      getAttributeStore: (level) =>
        communicationStore?.stores?.attributeEdit?.attributeValues[level],
    });
  }

  getLayerPossibilities = (excluded) => {
    const { general, layersConstraint } = this.props;
    const { filterLevel = [] } = general;
    return this.getPossibleLayers({
      filterLevel: filterLevel,
      filterValues: this._getFilterValues(filterLevel),
      layersConstraint: layersConstraint,
      excluded,
    });
  };

  /**
   *
   * @param {object} layer Infos über den Layer aus der LayerFilterconfig (mit den Constraints!)
   * @param {Array} filterLevel attribute
   * @param {object} filterValue Werte dieser Attribute (assoziatives array)
   * @returns True, wenn die Constraints des Layers mit den filterValues erfüllt werden
   */
  fillsConstraints({ layer, filterLevel, filterValues }) {
    return layerMatchesConstraints({
      layer,
      filterLevel,
      filterValues,
    });
  }

  fillsConstraintsExcept({
    layer,
    filterLevel,
    filterValues,
    excluded,
    defaultUrl,
  }) {
    return layerMatchesConstraintsExcept({
      layer,
      filterLevel,
      filterValues,
      excluded,
      defaultUrl,
    });
  }

  /**
   *
   * @param {object} layersConstraint Array an definierten Layern mit layerID (ggf. zusatzinfos) und constraints
   * @param {Array} filterLevel attribute
   * @param {object} filterValue Werte dieser Attribute (assoziatives array)
   * @returns {Object[]]} Layerobjecte, deren constraints erfüllt werden
   */
  getLayerIds(params) {
    const {
      filterLevel = [],
      filterValues = {},
      layersConstraint = [],
    } = params;
    return layersConstraint.filter((layer) =>
      this.fillsConstraints({ layer, filterLevel, filterValues })
    );
  }

  /** Clear a timer field if present and reset its reference to null. */
  _clearTimer(timerFieldName) {
    if (this[timerFieldName] === null || this[timerFieldName] === undefined) {
      return;
    }
    window.clearTimeout(this[timerFieldName]);
    this[timerFieldName] = null;
  }

  _setMapLayerTransition(message) {
    this.props.CommunicationStore?.addMessage({
      to: MAP_LAYER_TRANSITION_MESSAGE,
      message,
    });
  }

  /**
   * Evaluate unresolved filter state lazily when needed.
   * This avoids storing duplicate state that can become stale.
   */
  _hasUnresolvedFilterValues() {
    const filterLevel = this.props.general?.filterLevel || [];
    return hasUnresolvedFilterValuesInSet(this._getFilterValues(filterLevel));
  }

  /**
   * Stop the currently active transition and clear all transition-owned resources.
   * Safe to call repeatedly.
   */
  _cancelTransition(reason = "unspecified") {
    if (this._transitionHandle) {
      this._transitionHandle.remove();
      this._transitionHandle = null;
    }

    this._clearTimer("_postCommitCleanupTimer");

    if (this._transitionExportUnsubscribe) {
      this._transitionExportUnsubscribe();
      this._transitionExportUnsubscribe = null;
    }
    this._setMapLayerTransition(null);
    this._transitionKeepId = null;
  }

  _getFilterValues(filterLevel = []) {
    return filterLevel.map((level) => this.getLabelValue(level));
  }

  _hasRequiredPrimarySelection() {
    const attributeValues =
      this.props.CommunicationStore?.stores?.attributeEdit?.attributeValues ||
      {};

    // Explicit business rule: no raster unless all three are selected.
    return ["Kategorie", "Parameter", "Zeitabschnitt"].every((id) => {
      const value = attributeValues[id]?.value;
      if (value === undefined || value === null) return false;
      if (typeof value === "string" && value.trim() === "") return false;
      return true;
    });
  }

  _resolveInternalId(configId) {
    if (configId === undefined) return undefined;
    const key = String(configId);
    if (!this._configIdToInternalId.has(key)) {
      this._configIdToInternalId.set(key, this._internalIDOffset++);
    }
    return this._configIdToInternalId.get(key);
  }

  _getTargetOpacity(map) {
    const communicationStore = this.props.CommunicationStore;
    const operationalOpacity = communicationStore?.getMessagesToID({
      to: "operationalOpacity",
    })?.message;

    const targetOpacity = resolveTargetLayerOpacity({
      layers: this.layers,
      currentVisibleLayerId: this.currentVisibleLayerId,
      operationalOpacity,
      map,
      isLayerOnMap: APIHelper.isLayerOnMap,
      lastKnownOperationalOpacity: this._lastOperationalOpacity,
      defaultOpacity: DEFAULT_LAYER_OPACITY,
    });

    if (targetOpacity > 0) {
      this._lastOperationalOpacity = targetOpacity;
    }

    return targetOpacity;
  }

  _deactivateAllExcept(keepId, map) {
    deactivateAllLayersExcept({
      layers: this.layers,
      keepId,
      map,
      isLayerOnMap: APIHelper.isLayerOnMap,
      removeFromMap: (layerToRemove, mapInstance) =>
        this.removeFromMap(layerToRemove, mapInstance),
    });
  }

  _dimAllExcept(keepId, map, maxOpacity = OVERLAP_DIM_OPACITY) {
    dimAllLayersExcept({
      layers: this.layers,
      keepId,
      map,
      isLayerOnMap: APIHelper.isLayerOnMap,
      maxOpacity,
    });
  }

  /**
   * Cleanup old layers after commit.
   *
   * The cleanup waits briefly so the newly committed image can stabilize and,
   * if needed, polls until follow-up export requests settle.
   */
  _scheduleCleanupOfOldLayers({
    keepId,
    map,
    minDelayMs = CLEANUP_MIN_DELAY_MS,
    pollDelayMs = CLEANUP_POLL_DELAY_MS,
    maxDelayMs = CLEANUP_MAX_DELAY_MS,
    exportBaselinePending = 0,
    reason = "unspecified",
  }) {
    this._clearTimer("_postCommitCleanupTimer");

    const scheduledAt = Date.now();

    const runCleanup = () => {
      this._postCommitCleanupTimer = null;

      const elapsedMs = Date.now() - scheduledAt;
      const currentPending =
        mapServerExportMonitor.getState().exportPendingCount;

      // Keep the previous raster only while follow-up exports are still in-flight.
      if (elapsedMs < maxDelayMs && currentPending > exportBaselinePending) {
        this._postCommitCleanupTimer = window.setTimeout(
          runCleanup,
          pollDelayMs
        );
        return;
      }

      // If another transition started, let that transition own cleanup.
      if (this._transitionKeepId !== null) {
        return;
      }

      // If visible layer changed in the meantime, avoid stale cleanup.
      if (this.currentVisibleLayerId !== keepId) {
        this._setMapLayerTransition(null);
        return;
      }

      this._deactivateAllExcept(keepId, map);
      this._setMapLayerTransition(null);
    };

    this._postCommitCleanupTimer = window.setTimeout(runCleanup, minDelayMs);
  }

  /**
   * Handle temporary and real "no-layer" states.
   *
   * A short delay prevents blank flashes while filter updates are still settling.
   */
  _handleNoLayerSelection(map, { allowFallbackOnUnresolved = true } = {}) {
    if (!allowFallbackOnUnresolved) {
      this._cancelTransition("incomplete-primary-selection");
      this._clearTimer("_noLayerClearTimer");
      this._deactivateAllExcept(null, map);
      this.currentVisibleLayerId = null;
      return;
    }

    // A transient no-layer state can occur while attribute updates are still settling.
    // Never cancel an active transition based on this temporary state.
    if (this._transitionKeepId !== null) {
      return;
    }

    this._clearTimer("_noLayerClearTimer");

    // During first uncached pass, filter constraints can briefly yield no match
    // while async updates are in-flight. Delay clearing so we do not flash blank.
    this._noLayerClearTimer = window.setTimeout(() => {
      this._noLayerClearTimer = null;
      if (this._transitionKeepId !== null) {
        return;
      }

      if (this._hasUnresolvedFilterValues()) {
        const fallbackLayerId =
          this.currentVisibleLayerId ?? this._lastResolvedLayerId;
        const fallbackLayer =
          fallbackLayerId !== null ? this.layers[fallbackLayerId] : null;

        if (fallbackLayer) {
          const wasOnMap = APIHelper.isLayerOnMap(map, fallbackLayer);
          if (!wasOnMap) {
            this.addToMap(fallbackLayer, map);
          }
          const currentOpacity = Number(fallbackLayer.opacity);
          if (!Number.isFinite(currentOpacity) || currentOpacity <= 0) {
            const restoredOpacity = this._getTargetOpacity(map);
            fallbackLayer.setOpacity(
              restoredOpacity > 0 ? restoredOpacity : DEFAULT_LAYER_OPACITY
            );
            if (restoredOpacity > 0) {
              this._lastOperationalOpacity = restoredOpacity;
            }
          }
          this.currentVisibleLayerId = fallbackLayerId;
          return;
        }
        return;
      }

      // Keep the last visible layer if it is still on map to avoid blank flashes
      // from short-lived "no layer" intermediate states.
      if (this.currentVisibleLayerId !== null) {
        const currentLayer = this.layers[this.currentVisibleLayerId];
        if (currentLayer) {
          const wasOnMap = APIHelper.isLayerOnMap(map, currentLayer);
          if (!wasOnMap) {
            this.addToMap(currentLayer, map);
          }
          return;
        }
      }

      this._deactivateAllExcept(null, map);
      this.currentVisibleLayerId = null;
    }, NO_LAYER_CLEAR_DELAY_MS);
  }

  _ensureManagedLayerForConfig({ layerConfig, map, baseUrl, token, view }) {
    const { id, name, url, constraints } = layerConfig;
    const _internalID = this._resolveInternalId(id);

    if (!this.layers[_internalID]) {
      const { layerBaseUrl, layerId } = resolveLayerSource({
        baseUrl,
        id,
        url,
      });

      const createdLayer = this.createLayer({
        baseUrl: layerBaseUrl,
        id: layerId,
        token,
        name,
        map,
        view,
      });
      createdLayer.constraints = constraints;
      createdLayer.setOpacity(0);
      this.layers[_internalID] = createdLayer;
    } else {
      this.layers[_internalID].constraints = constraints;
    }

    this._seedLegendFromCache(layerConfig, this.layers[_internalID]);

    return this.layers[_internalID];
  }

  _isTransitionCurrent(keepId, transitionSeq) {
    return (
      this._transitionKeepId === keepId &&
      this._activeTransitionSeq === transitionSeq
    );
  }

  /**
   * Mark transition as completed and release watchers/subscriptions.
   */
  _finalizeTransition({ keepId, transitionSeq, reason = "unspecified" }) {
    if (!this._isTransitionCurrent(keepId, transitionSeq)) {
      return;
    }

    if (this._transitionExportUnsubscribe) {
      this._transitionExportUnsubscribe();
      this._transitionExportUnsubscribe = null;
    }

    this._transitionHandle = null;
    if (reason !== "commit-complete") {
      this._setMapLayerTransition(null);
    }
    this._transitionKeepId = null;
  }

  _commitVisibleLayer(transitionData, reason = "unspecified") {
    // Perform the visible swap on paint ticks so the browser has time to
    // present the freshly loaded image before old layers are cleaned up.
    const {
      keepId,
      transitionSeq,
      map,
      newLayerWrapper,
      exportBaselinePending,
    } = transitionData;

    if (!this._isTransitionCurrent(keepId, transitionSeq)) {
      return;
    }

    requestAnimationFrame(() => {
      if (!this._isTransitionCurrent(keepId, transitionSeq)) return;
      requestAnimationFrame(() => {
        if (!this._isTransitionCurrent(keepId, transitionSeq)) return;
        // Commit after two paint ticks for first-load stability on uncached exports.
        // Re-read opacity at commit time so any slider drag that happened during
        // the transition (which updated CommunicationStore) is respected rather
        // than silently overwritten by the stale value captured at transition start.
        const commitOpacity = this._getTargetOpacity(map);
        newLayerWrapper.setOpacity(commitOpacity);
        if (commitOpacity > 0) {
          this._lastResolvedLayerId = keepId;
          this._lastOperationalOpacity = commitOpacity;
        }
        // Keep the old image for one more paint tick to avoid first-pass blanking.
        requestAnimationFrame(() => {
          if (!this._isTransitionCurrent(keepId, transitionSeq)) return;

          // Immediately reduce old-layer visibility to avoid perceptual
          // double-image flicker while still keeping a short safety overlap.
          this._dimAllExcept(keepId, map, OVERLAP_DIM_OPACITY);

          this._scheduleCleanupOfOldLayers({
            keepId,
            map,
            minDelayMs: CLEANUP_MIN_DELAY_MS,
            pollDelayMs: CLEANUP_POLL_DELAY_MS,
            maxDelayMs: CLEANUP_MAX_DELAY_MS,
            exportBaselinePending,
            reason: "post-commit-stabilize",
          });

          this._finalizeTransition({
            keepId,
            transitionSeq,
            reason: "commit-complete",
          });
        });
      });
    });
  }

  _waitForExportSettleThenCommit(transitionData, reason = "unspecified") {
    // Wait until export traffic returns to baseline to avoid committing while
    // the target image is still being rendered server-side.
    const { keepId, transitionSeq, exportBaselinePending } = transitionData;

    if (!this._isTransitionCurrent(keepId, transitionSeq)) {
      return;
    }

    const currentPending = mapServerExportMonitor.getState().exportPendingCount;

    if (currentPending <= exportBaselinePending) {
      this._commitVisibleLayer(transitionData, "export-settle-immediate");
      return;
    }

    if (this._transitionExportUnsubscribe) {
      this._transitionExportUnsubscribe();
      this._transitionExportUnsubscribe = null;
    }

    let lastPendingCount = null;
    this._transitionExportUnsubscribe = mapServerExportMonitor.subscribe(
      (state) => {
        if (!this._isTransitionCurrent(keepId, transitionSeq)) return;

        if (state.exportPendingCount !== lastPendingCount) {
          lastPendingCount = state.exportPendingCount;
        }

        if (state.exportPendingCount <= exportBaselinePending) {
          if (this._transitionExportUnsubscribe) {
            this._transitionExportUnsubscribe();
            this._transitionExportUnsubscribe = null;
          }
          this._commitVisibleLayer(transitionData, "export-settled");
        }
      }
    );
  }

  /**
   * Wait until ArcGIS layerView signals that the new image has finished updating,
   * then continue with export-settle/commit logic.
   */
  _setupLayerViewTransition(transitionData) {
    const {
      keepId,
      transitionSeq,
      map,
      view,
      newLayerWrapper,
      layerAlreadyOnMap,
      targetOpacity,
    } = transitionData;

    let watchHandle = null;
    let settleTimer = null;
    const timeoutId = window.setTimeout(() => {
      this._commitVisibleLayer(transitionData, "timeout");
    }, TRANSITION_TIMEOUT_MS);

    this._transitionHandle = {
      remove: () => {
        if (watchHandle) {
          watchHandle.remove();
          watchHandle = null;
        }
        if (settleTimer) {
          window.clearTimeout(settleTimer);
          settleTimer = null;
        }
        if (this._transitionExportUnsubscribe) {
          this._transitionExportUnsubscribe();
          this._transitionExportUnsubscribe = null;
        }
        window.clearTimeout(timeoutId);
      },
    };

    view
      .whenLayerView(newLayerWrapper.layer)
      .then((layerView) => {
        let seenUpdating = layerView.updating;
        let finalized = false;

        const finalize = (reason) => {
          if (finalized) return;
          finalized = true;
          if (watchHandle) {
            watchHandle.remove();
            watchHandle = null;
          }
          if (settleTimer) {
            window.clearTimeout(settleTimer);
            settleTimer = null;
          }
          window.clearTimeout(timeoutId);

          this._waitForExportSettleThenCommit(
            transitionData,
            `layerView-${reason}`
          );
        };

        if (layerAlreadyOnMap && !layerView.updating) {
          finalize("already-on-map-not-updating");
          return;
        }

        // `whenLayerView` can resolve before the MapServer/export request has
        // started, so `updating` may be false even though no image is loaded yet.
        // Track whether the layerView actually enters a loading cycle; only
        // finalize on the transition from true -> false (image fully loaded).
        watchHandle = layerView.watch("updating", (isUpdating) => {
          if (isUpdating) {
            seenUpdating = true;
          } else if (seenUpdating) {
            finalize("updating-cycle-complete");
          }
        });

        // If request start is delayed (first uncached round), do not finalize
        // immediately. Wait a little to allow `updating=true` to appear.
        // If it never appears, treat this as a warm cached layer and finalize.
        if (!seenUpdating) {
          settleTimer = window.setTimeout(() => {
            if (!seenUpdating && !layerView.updating) {
              finalize("settle-fallback");
            }
          }, LAYERVIEW_SETTLE_FALLBACK_MS);
        }
      })
      .catch((error) => {
        window.clearTimeout(timeoutId);
        newLayerWrapper.setOpacity(targetOpacity);
        this._deactivateAllExcept(keepId, map);
        this._finalizeTransition({
          keepId,
          transitionSeq,
          reason: "layerView-error",
        });
      });
  }

  /**
   * Start a new guarded layer transition.
   *
   * The new layer is added hidden first (opacity 0), then committed once the
   * loading + export pipeline is ready.
   */
  _startLayerTransition({
    keepId,
    layerId,
    map,
    view,
    newLayerWrapper,
    targetOpacity,
    layerName,
  }) {
    this._cancelTransition("start-new-transition");

    const transitionSeq = this._activeTransitionSeq + 1;
    this._activeTransitionSeq = transitionSeq;
    this._transitionKeepId = keepId;

    // Keep current raster visible while next raster is loading.
    newLayerWrapper.setOpacity(0);
    const layerAlreadyOnMap = APIHelper.isLayerOnMap(map, newLayerWrapper);
    this.addToMap(newLayerWrapper, map);
    this.currentVisibleLayerId = keepId;

    const exportBaselinePending =
      mapServerExportMonitor.getState().exportPendingCount;

    const transitionData = {
      keepId,
      layerId,
      transitionSeq,
      map,
      view,
      newLayerWrapper,
      targetOpacity,
      exportBaselinePending,
      layerAlreadyOnMap,
    };

    this._setMapLayerTransition({
      busy: true,
      layerId,
      layerName:
        layerName || newLayerWrapper.displayName || newLayerWrapper.name,
      internalId: keepId,
      transitionSeq,
    });

    if (
      view &&
      newLayerWrapper.layer &&
      typeof view.whenLayerView === "function"
    ) {
      this._setupLayerViewTransition(transitionData);
      return;
    }

    newLayerWrapper.setOpacity(targetOpacity);
    this._deactivateAllExcept(keepId, map);
    this._finalizeTransition({
      keepId,
      transitionSeq,
      reason: "no-layer-view",
    });
  }

  showLayer(layerC = {}, { allowFallbackOnUnresolved = true } = {}) {
    const { map, baseUrl, token, view } = this.props;

    if (!layerC || layerC.id === undefined) {
      this._handleNoLayerSelection(map, { allowFallbackOnUnresolved });
      return;
    }

    this._clearTimer("_noLayerClearTimer");

    const { id } = layerC;
    if (id === undefined) return;
    const _internalID = this._resolveInternalId(id);

    const newLayerWrapper = this._ensureManagedLayerForConfig({
      layerConfig: layerC,
      map,
      baseUrl,
      token,
      view,
    });

    const targetOpacity = this._getTargetOpacity(map);
    const previousVisibleId = this.currentVisibleLayerId;

    if (previousVisibleId === _internalID && this._transitionKeepId === null) {
      this.addToMap(newLayerWrapper, map);
      if (newLayerWrapper.opacity !== targetOpacity) {
        newLayerWrapper.setOpacity(targetOpacity);
      }
      if (targetOpacity > 0) {
        this._lastResolvedLayerId = _internalID;
        this._lastOperationalOpacity = targetOpacity;
      }

      const hasOtherMapLayers = hasOtherManagedMapLayers({
        layers: this.layers,
        map,
        isLayerOnMap: APIHelper.isLayerOnMap,
        excludeInternalId: _internalID,
      });

      if (hasOtherMapLayers) {
        this._deactivateAllExcept(_internalID, map);
      }
      return;
    }

    // Repeated updates often call showLayer again for the same target while
    // its transition is still in-flight. Restarting would hide the same layer
    // (opacity=0), cancel watchers, and can produce visible blinking/blanking.
    if (this._transitionKeepId === _internalID) {
      return;
    }

    this._startLayerTransition({
      keepId: _internalID,
      layerId: id,
      map,
      view,
      newLayerWrapper,
      targetOpacity,
      layerName: layerC.name,
    });
  }

  getPossibleLayers = ({
    filterLevel = [],
    filterValues = [],
    layersConstraint = [],
    excluded = {}, // key: [values]
  }) => {
    return layersConstraint
      .map((layer) =>
        this.fillsConstraintsExcept({
          layer,
          filterLevel,
          filterValues,
          excluded,
          defaultUrl: layer.url || `${this.props.baseUrl}/${layer.id}`,
        })
      )
      .filter((l) => l);
  };

  /**
   *
   * @param {*} level die ebene deren label und values zurüclgegeben werden soll
   * @returns label und value des dropdowns welches zu dem levewl gehört
   */
  getLabelValue = (level) => {
    const communicationStore = this.props.CommunicationStore;
    const attrStore =
      communicationStore.stores.attributeEdit.attributeValues[level];
    const value = attrStore.value;
    const choices = attrStore.updateOptions?.choices;
    const labels = choices?.map(({ label, value }) => label);
    const values = choices?.map(({ label, value }) => value);
    const label = labels ? labels[values?.indexOf(value)] : undefined;
    return {
      label: label,
      value: value,
    };
  };

  createLayer({ baseUrl, id, token = "dummy", options = {}, name, map, view }) {
    const layer = LayerStore.addLayer(
      APIHelper.createImageLayer(
        { name, url: baseUrl + "/" + id },
        { token },
        {
          opacity: DEFAULT_LAYER_OPACITY,
          imageFormat: "png8",
          refreshInterval: 0,
          ...options,
        }
      ),
      map,
      view
    );

    // Keep a stable, human-friendly label from config (wrapper.name may reflect service title).
    layer.displayName = name;

    // Mark this layer as owned by LayerFilter for filtering purposes
    layer.isLayerFilterManaged = true;

    return layer;
  }

  /**
   * Add a layer to the map
   * Only adds if not already in the map to prevent duplicate DOM operations
   */
  addToMap(layer, map) {
    // Only add if not already in map
    if (!layer.layer.map) {
      layer.addToMap(map);
      // Catch server-timeout / network errors so they don't surface as
      // unhandled promise rejections (ArcGIS triggers load when added to map).
      layer.layer.load().catch((error) => {
        if (error?.name !== "AbortError") {
          console.warn(
            `[LayerFilter] Layer "${
              layer.displayName || layer.layer.url
            }" konnte nicht geladen werden:`,
            error?.message || error
          );
        }
      });
    }
  }

  removeFromMap(layer, map) {
    layer.removeFromMap(map);
  }

  /**
   * Resolve the currently matching layer configuration and synchronize map +
   * "shownLayers" communication message.
   */
  _syncShownLayer() {
    const { CommunicationStore, general, layersConstraint } = this.props;
    const { filterLevel } = general;

    const hasPrimarySelection = this._hasRequiredPrimarySelection();

    if (!hasPrimarySelection) {
      this._clearStableLegend();
      this.showLayer(null, { allowFallbackOnUnresolved: false });

      if (this._lastShownLayerId !== null) {
        this._lastShownLayerId = null;
        CommunicationStore.addMessage({
          to: "shownLayers",
          message: {
            layerInfos: [],
          },
        });
      }
      return;
    }

    const filterValues = this._getFilterValues(filterLevel);

    const layerIds = this.getLayerIds({
      layersConstraint,
      filterLevel,
      filterValues,
    });

    if (layerIds.length > 1) {
      console.warn(
        `[LayerFilter] ${
          layerIds.length
        } layers match current selection. Using last match (id: ${
          layerIds[layerIds.length - 1]?.id
        }).`
      );
    }

    const currentLayerCfg =
      layerIds.length > 0 ? layerIds[layerIds.length - 1] : null;

    // Primary selection is complete but no layer constraint matches (e.g. the
    // current Zeitraum has no raster for the new Kategorie/Parameter/Zeitabschnitt).
    // Treat this identically to the incomplete-primary-selection case: clear the
    // map immediately without any fallback restore.
    if (!currentLayerCfg) {
      this._clearStableLegend();
      this.showLayer(null, { allowFallbackOnUnresolved: false });
      if (this._lastShownLayerId !== null) {
        this._lastShownLayerId = null;
        CommunicationStore.addMessage({
          to: "shownLayers",
          message: { layerInfos: [] },
        });
      }
      return;
    }

    this.showLayer(currentLayerCfg);

    const resolvedLayerId = currentLayerCfg.id ?? null;
    if (resolvedLayerId !== this._lastShownLayerId) {
      this._lastShownLayerId = resolvedLayerId;

      CommunicationStore.addMessage({
        to: "shownLayers",
        message: {
          layerInfos: [
            {
              layerId: currentLayerCfg.id,
              layer: this.layers[this._resolveInternalId(currentLayerCfg.id)],
              metaData: currentLayerCfg,
            },
          ],
        },
      });
    }
  }

  /**
   * Restore Zeitabschnitt on same-category parameter switches.
   * When DropDown clears Zeitabschnitt after a parameter switch, Kategorie is
   * still the same — that's the signal to restore the previously selected label.
   * autoSelectSingleChoice handles the 1-option fallback automatically.
   */
  _setupZeitabschnittPreserve() {
    const attrValues =
      this.props.CommunicationStore?.stores?.attributeEdit?.attributeValues;
    if (!attrValues?.["Zeitabschnitt"]) return;

    // Seed the label cache from the current value at mount time.
    const initialChoices =
      attrValues["Zeitabschnitt"].updateOptions?.choices ?? [];
    let lastLabel =
      findChoiceByValue(initialChoices, attrValues["Zeitabschnitt"].value)
        ?.label ?? null;
    // Sticky flag: Kategorie changed since the last time Z settled.
    // Needed because K and Z changes arrive in separate callbacks:
    //   Callback 1 — K changes, Z still holds the old value → flag set
    //   Callback 2 — DropDown clears Z → flag prevents a false restore
    let kategorieJustChanged = false;

    this._zeitabschnittPreserveDisposer = reaction(
      () => ({
        zeitabschnittValue: attrValues["Zeitabschnitt"]?.value,
        kategorieValue: attrValues["Kategorie"]?.value,
      }),
      (current, previous) => {
        const kChangedNow = !isSameValue(
          current.kategorieValue,
          previous.kategorieValue
        );
        if (kChangedNow) kategorieJustChanged = true;

        if (isSet(current.zeitabschnittValue)) {
          if (!kChangedNow) {
            // Z settled under a stable Kategorie — safe to cache this label.
            const label = findChoiceByValue(
              attrValues["Zeitabschnitt"].updateOptions?.choices ?? [],
              current.zeitabschnittValue
            )?.label;
            if (label) lastLabel = label;
            kategorieJustChanged = false;
          }
          // If K also changed in this tick, leave kategorieJustChanged = true so
          // the upcoming Z-clear callback knows not to restore. If Z stays set
          // (integer coincidentally valid in new choices), the flag is cleared
          // when Z next settles under stable K (the !kChangedNow branch above).
        } else if (
          isSet(previous.zeitabschnittValue) &&
          isSameValue(current.kategorieValue, previous.kategorieValue) &&
          !kategorieJustChanged
        ) {
          // Z cleared, K stable in this tick and K did not recently change → P switch.
          // Fails silently if label is gone; autoSelectSingleChoice handles 1-option case.
          preserveZeitabschnittByLabel({
            zeitabschnittStore: attrValues["Zeitabschnitt"],
            savedLabel: lastLabel,
          });
          kategorieJustChanged = false;
        } else {
          // Z cleared due to K change (or other reason) — consume the flag.
          kategorieJustChanged = false;
        }
      }
    );
  }

  /**
   * Resets Szenario and Characteristic to their config-declared defaults whenever
   * Kategorie, Parameter, or Zeitabschnitt changes. The reset is deferred until
   * the Filter dependency chain has activated a projection Zeitraum path —
   * i.e. real, non-skip choices are present in both the Szenario and
   * Characteristic attribute stores.
   *
   * WHY HERE, not in KlimaSliderWrapper:
   *   This is business logic, not rendering. LayerFilter owns the filter-state
   *   bridge, has direct store access, and has clean lifecycle hooks for
   *   reaction disposal. KlimaSliderWrapper should be a pure rendering adapter.
   *
   * WHY DIRECT setValue ON BOTH attributes:
   *   Resetting through the Szenario→Characteristic cascade silently fails when
   *   Szenario.value is already the target integer — MobX skips same-value
   *   notifications, so Characteristic never resets via the cascade.
   */
  _setupDefaultReset() {
    const attrValues =
      this.props.CommunicationStore?.stores?.attributeEdit?.attributeValues;
    if (!attrValues) return;

    let cancelPendingWhen = null;

    this._kpzResetDisposer = reaction(
      () =>
        [
          attrValues["Kategorie"]?.value,
          attrValues["Parameter"]?.value,
          attrValues["Zeitabschnitt"]?.value,
        ].join("||"),
      () => {
        // Cancel any previous pending when-reset (handles rapid K/P/Z changes).
        cancelPendingWhen?.();

        // Wait until the Filter dependency chain has loaded real projection
        // choices — this happens when the slider reaches a projection Zeitraum.
        cancelPendingWhen = when(
          () => {
            // Guard 1: the current Zeitraum value must be present in the
            // current Zeitraum choices AND be a projection period (has "default").
            // Without this check, the when fires immediately after a K/P/Z change
            // while Szenario choices are still the OLD path's non-skip choices but
            // the Zeitraum updateOptions have already been updated with new-path
            // integers — causing the value lookup to return undefined and silently
            // skipping the reset.
            const zeitraumChoices =
              attrValues["Zeitraum"]?.updateOptions?.choices ?? [];
            const currentZeitraum = zeitraumChoices.find(
              (c) => c.value === attrValues["Zeitraum"]?.value
            );
            if (!currentZeitraum?.default) return false;

            // Guard 2: Szenario and Characteristic must have real (non-skip)
            // choices — meaning the Filter chain has activated a projection path.
            const sz = attrValues["Szenario"]?.updateOptions?.choices ?? [];
            const ch =
              attrValues["Characteristic"]?.updateOptions?.choices ?? [];
            return (
              sz.length > 0 &&
              sz.some((c) => c.label?.toLowerCase() !== "skip") &&
              ch.length > 0 &&
              ch.some((c) => c.label?.toLowerCase() !== "skip")
            );
          },
          () => {
            cancelPendingWhen = null;

            // Re-read (condition already verified these exist).
            const zeitraumChoices =
              attrValues["Zeitraum"]?.updateOptions?.choices ?? [];
            const currentZeitraum = zeitraumChoices.find(
              (c) => c.value === attrValues["Zeitraum"]?.value
            );
            const defaultSzenarioLabel = currentZeitraum?.default;

            // Reset Szenario and read the config-declared Characteristic default
            // from the matched Szenario choice node's own "default" property.
            const szenarioChoices =
              attrValues["Szenario"]?.updateOptions?.choices ?? [];
            const szenarioTarget = defaultSzenarioLabel
              ? szenarioChoices.find((c) => c.label === defaultSzenarioLabel)
              : null;
            if (szenarioTarget)
              attrValues["Szenario"].setValue(szenarioTarget.value);

            // Reset Characteristic AFTER yielding to MobX.
            // Characteristic choices are populated by the Filter dependency
            // chain triggered by Szenario.setValue above. That chain runs
            // synchronously inside MobX reactions (registered before ours),
            // so it fires — and calls setAttributeOptions on Characteristic —
            // AFTER we return from this callback. A setTimeout(0) ensures our
            // setValue runs once the new RCP8.5 Characteristic choices are
            // already in place, not before.
            //
            // defaultCharLabel comes from the matched Szenario node's own
            // "default" property — fully config-driven, no hardcoded strings.
            const defaultCharLabel = szenarioTarget?.default;
            setTimeout(() => {
              if (!defaultCharLabel) return;
              const charChoices =
                attrValues["Characteristic"]?.updateOptions?.choices ?? [];
              const charTarget = charChoices.find(
                (c) => c.label?.toLowerCase() === defaultCharLabel.toLowerCase()
              );
              if (charTarget)
                attrValues["Characteristic"].setValue(charTarget.value);
            }, 0);
          }
        );
      }
    );
  }

  componentDidMount() {
    this._syncShownLayer();
    this._setupDefaultReset();
    this._setupZeitabschnittPreserve();
  }

  componentDidUpdate() {
    // Debounce layer-swap requests during rapid slider scrubbing.
    // Without this, every intermediate slider position fires an export request
    // while a previous one is still in-flight, saturating the network.
    // SYNC_DEBOUNCE_MS is imperceptible to the user but prevents dozens of concurrent
    // MapServer/export calls when dragging the slider quickly.
    this._clearTimer("_syncDebounceTimer");

    this._syncDebounceTimer = window.setTimeout(() => {
      this._syncDebounceTimer = null;
      this._syncShownLayer();
    }, SYNC_DEBOUNCE_MS);

    // If no required primary selection exists anymore, clear any manual
    // expansion override so the next valid legend can auto-expand again.
    if (
      !this._hasRequiredPrimarySelection() &&
      this.state.legendPanelUserOverride !== null
    ) {
      this.setState({ legendPanelUserOverride: null });
    }

    // Store legend in cache here, not in render() — render must be pure.
    const { general, layersConstraint } = this.props;
    const { filterLevel } = general;
    const layerIds = this.getLayerIds({
      layersConstraint,
      filterLevel,
      filterValues: this._getFilterValues(filterLevel),
    });
    const lastMatch =
      layerIds.length > 0 ? layerIds[layerIds.length - 1] : null;
    const currentLayerInternalId = lastMatch
      ? this._resolveInternalId(lastMatch.id)
      : null;
    const layer =
      currentLayerInternalId != null
        ? this.layers?.[currentLayerInternalId]
        : null;
    if (lastMatch && layer) {
      this._storeLegendInCache(lastMatch, layer);
    }
  }

  componentWillUnmount() {
    this._kpzResetDisposer?.();
    this._zeitabschnittPreserveDisposer?.();
    const { map } = this.props;
    this._clearTimer("_syncDebounceTimer");
    this._clearTimer("_noLayerClearTimer");
    this._clearTimer("_postCommitCleanupTimer");
    this._cancelTransition("componentWillUnmount");
    Object.values(this.layers).forEach((layer) => {
      if (layer && APIHelper.isLayerOnMap(map, layer)) {
        this.removeFromMap(layer, map);
      }
    });
    this.layers = {};
    this._legendInfoCache.clear();
    this._clearStableLegend();
    this.currentVisibleLayerId = null;
  }

  render() {
    const { CommunicationStore, general, layersConstraint, map, view } =
      this.props;
    const { filterLevel } = general;

    const layerIds = this.getLayerIds({
      layersConstraint,
      filterLevel,
      filterValues: this._getFilterValues(filterLevel),
    });

    const lastMatch =
      layerIds.length > 0 ? layerIds[layerIds.length - 1] : null;
    const currentLayerInternalId = lastMatch
      ? this._resolveInternalId(lastMatch.id)
      : null;
    const layer =
      currentLayerInternalId != null
        ? this.layers?.[currentLayerInternalId]
        : null;

    const legendCacheKey = lastMatch
      ? this._getLegendCacheKey(lastMatch)
      : null;
    const cachedLegendInfo = legendCacheKey
      ? this._legendInfoCache.get(legendCacheKey)
      : null;
    let legendForSummary = layer?.legendInfo || cachedLegendInfo;
    let layerForLegend = layer;
    const canReuseLastStableLegend =
      Boolean(legendCacheKey) &&
      Boolean(this._lastStableLegendCacheKey) &&
      legendCacheKey === this._lastStableLegendCacheKey;

    if (legendForSummary) {
      this._lastStableLegendInfo = legendForSummary;
      if (legendCacheKey) {
        this._lastStableLegendCacheKey = legendCacheKey;
      }
      if (layer?.url) {
        this._lastStableLegendLayerUrl = layer.url;
      }

      // During period switches, the new wrapper can be temporarily unavailable
      // for one render even though we already have equivalent cached legend data.
      // Always provide a legend-bearing fallback layer to avoid noData flashes.
      if (!layer || !layer.legendInfo) {
        layerForLegend = {
          url:
            layer?.url ||
            this._lastStableLegendLayerUrl ||
            "layerfilter-stable-legend",
          legendInfo: legendForSummary,
          legendState: "loaded",
        };
      }
    } else if (
      canReuseLastStableLegend &&
      this._shouldKeepPreviousLegend(layerIds.length) &&
      this._lastStableLegendInfo
    ) {
      legendForSummary = this._lastStableLegendInfo;
      layerForLegend = {
        url:
          layer?.url ||
          this._lastStableLegendLayerUrl ||
          "layerfilter-stable-legend",
        legendInfo: this._lastStableLegendInfo,
        legendState: "loaded",
      };
    }
    const isLayerTransitioning =
      currentLayerInternalId !== null &&
      this._transitionKeepId === currentLayerInternalId;

    // During fast period switches, the new target wrapper can be briefly missing.
    // Keep opacity UI bound to the last known real wrapper to avoid slider flashes.
    const fallbackOpacityLayerId =
      this.currentVisibleLayerId ??
      this._lastResolvedLayerId ??
      this._transitionKeepId;
    const fallbackOpacityLayer =
      fallbackOpacityLayerId !== null && fallbackOpacityLayerId !== undefined
        ? this.layers?.[fallbackOpacityLayerId]
        : null;
    const layerForOpacity = layer || fallbackOpacityLayer;

    const storeOpacity = CommunicationStore.getMessagesToID({
      to: "operationalOpacity",
    })?.message;

    // Subscribe to shownLayers so that render() re-runs after _syncShownLayer
    // publishes the message (after this.layers[internalId] is populated).
    // this.layers is a plain object so MobX can't track it directly; this read
    // creates the dependency that drives the second render for new layers.
    CommunicationStore.getMessagesToID({ to: "shownLayers" });

    const legendInfo = legendForSummary;
    const isLegendFetchPending =
      Boolean(lastMatch) &&
      !legendInfo &&
      (isLayerTransitioning ||
        !layer ||
        (layer.legendState !== "loaded" && layer.legendState !== "error"));

    const shouldAutoKeepLegendPanelOpenForLoading =
      this.state.legendPanelUserOverride === null &&
      Boolean(this._lastStableLegendInfo) &&
      isLegendFetchPending;

    const shouldShowLegendLoadingState =
      isLegendFetchPending &&
      (shouldAutoKeepLegendPanelOpenForLoading ||
        this.state.legendPanelUserOverride === true);

    if (shouldShowLegendLoadingState) {
      // Keep content in loading mode to avoid noData flashes while fetching
      // a new legend after parameter/period switches.
      layerForLegend = {
        ...(layerForLegend || {}),
        url:
          layer?.url ||
          this._lastStableLegendLayerUrl ||
          "layerfilter-legend-loading",
        legendInfo: undefined,
        legendState: "loading",
      };
    }

    const legendPanelExpanded =
      this.state.legendPanelUserOverride ??
      Boolean(legendInfo || shouldAutoKeepLegendPanelOpenForLoading);

    return (
      <AccordionPanel
        summary={`Legende${
          legendInfo && legendInfo.layerName ? " - " + legendInfo.layerName : ""
        }`}
        expanded={legendPanelExpanded}
        onExpandedChange={this._handleLegendPanelExpandedChange}
      >
        <Legend
          map={map}
          style={{
            paddingLeft: 20,
            marginRight: 0,
            fontFamily: "Roboto, Helvetica, Arial, sans-serif",
            fontWeight: 400,
          }}
          view={view}
          layer={layerForLegend}
          CommunicationStore={CommunicationStore}
        />
        {layerIds.length > 0 && layerForOpacity && (
          <div
            style={{
              padding: "0px 16px 0px 4px",
              marginTop: 20,
              fontFamily: "Roboto, Helvetica, Arial, sans-serif",
              fontWeight: 400,
            }}
          >
            <Typography style={{ marginBottom: 5 }}>
              {"Deckkraft der Themen über der Grundkarte"}
            </Typography>
            <Observer>
              {() => {
                const parsedStoreOpacity = Number(storeOpacity);
                const hasStoreOpacity =
                  storeOpacity !== undefined &&
                  storeOpacity !== null &&
                  storeOpacity !== "" &&
                  !Number.isNaN(parsedStoreOpacity);
                const normalizedStoreOpacity = hasStoreOpacity
                  ? normalizeLayerOpacity(parsedStoreOpacity)
                  : null;
                const normalizedLayerOpacity = normalizeLayerOpacity(
                  layerForOpacity?.opacity
                );
                const preferredTransitionOpacity =
                  normalizedStoreOpacity ??
                  normalizeLayerOpacity(this._lastOperationalOpacity) ??
                  normalizedLayerOpacity ??
                  DEFAULT_LAYER_OPACITY;
                const sliderOpacity = isLayerTransitioning
                  ? preferredTransitionOpacity
                  : normalizedLayerOpacity ?? DEFAULT_LAYER_OPACITY;

                if (
                  layerForOpacity &&
                  !isLayerTransitioning &&
                  hasStoreOpacity &&
                  layerForOpacity.opacity !== parsedStoreOpacity
                ) {
                  layerForOpacity.setOpacity(parsedStoreOpacity);
                  if (
                    normalizedStoreOpacity !== null &&
                    normalizedStoreOpacity > 0
                  ) {
                    this._lastOperationalOpacity = normalizedStoreOpacity;
                  }
                }
                if (
                  !isLayerTransitioning &&
                  !hasStoreOpacity &&
                  layerForOpacity
                ) {
                  const layerOpacity = Number(layerForOpacity.opacity);
                  // Do not persist transient hidden-layer opacity (0) as global
                  // operational opacity; it can make all following layers invisible.
                  if (!Number.isNaN(layerOpacity) && layerOpacity > 0) {
                    this._lastOperationalOpacity = layerOpacity;
                    CommunicationStore.addMessage({
                      to: "operationalOpacity",
                      message: layerOpacity,
                    });
                  }
                }
                return (
                  <OpacitySlider
                    opacity={sliderOpacity * 100}
                    setOpacity={(opacity) => {
                      const nextOpacity = opacity / 100;
                      layerForOpacity.setOpacity(nextOpacity);
                      if (nextOpacity > 0) {
                        this._lastOperationalOpacity = nextOpacity;
                      }
                      CommunicationStore.addMessage({
                        to: "operationalOpacity",
                        message: nextOpacity,
                      });
                    }}
                  />
                );
              }}
            </Observer>
          </div>
        )}
      </AccordionPanel>
    );
  }
}
export default observer(LayerFilter);
